import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent'

const features = [
  {
    number: 1,
    title: "Effortless Project Creation",
    desc: "JJ Kit allows you to create and launch projects with unparalleled ease. Define project goals, milestones, and deliverables all in one place. Assign priorities, deadlines, and owners while tracking progress in real-time. Every project is organized, actionable, and fully visible to your team, ensuring nothing falls through the cracks."
  },
  {
    number: 2,
    title: "User Management and Role-Based Access",
    desc: "Manage your team effortlessly with JJ Kit’s smart user management. Add users, define roles, and control access levels so that everyone only sees what they need. From project managers to developers, permissions are clear, reducing errors and maintaining security across projects."
  },
  {
    number: 3,
    title: "Team Management and Collaboration",
    desc: "JJ Kit brings your team together in one unified workspace. Assign tasks, share updates, and communicate seamlessly across departments. Every member stays aligned on goals and responsibilities, fostering collaboration, accountability, and transparency throughout your organization."
  },
  {
    number: 4,
    title: "Advanced Reporting and Analytics",
    desc: "Gain actionable insights with JJ Kit’s powerful analytics engine. Monitor project progress, track sprint velocity, analyze Kanban board performance, and identify bottlenecks. Make data-driven decisions, improve efficiency, and showcase your team’s performance to stakeholders with real-time reports and dashboards."
  },
  {
    number: 5,
    title: "Scrum Backlog Management",
    desc: "Plan and prioritize every sprint effortlessly. JJ Kit lets you create detailed backlogs, assign tasks, set priorities, and balance workloads. Your Agile process stays efficient and adaptable, enabling teams to focus on what matters most without losing sight of deadlines."
  },
  {
    number: 6,
    title: "Interactive Kanban Boards",
    desc: "Visualize every task, stage, and milestone with drag-and-drop Kanban boards. Track progress at a glance, move tasks between stages, and ensure workflow clarity. Teams can instantly identify bottlenecks, stay on schedule, and achieve maximum productivity."
  },
  {
    number: 7,
    title: "Mobile Access",
    desc: "Manage your projects anytime, anywhere. JJ Kit’s mobile interface ensures that team members can create tasks, track progress, and communicate seamlessly on the go. Perfect for distributed teams and remote work."
  }
]

const JjKit = () => {
  return (
    <>
      <Helmet>
        <title>JJ Kit | Agile Project Management Software by Data Repo</title>
        <meta
          name="description"
          content="JJ Kit by Data Repo is an agile project management tool for Scrum and Kanban teams. Manage tasks, track progress, and improve team collaboration with real-time insights."
        />
      </Helmet>

      <section
        style={{
          backgroundImage: `url(../images/bg-casestudies-box.png)`,
          backgroundSize: "100% 80vh"
        }}
        className="hero--container relative flex flex-col justify-center w-full h-[60vh] bg-no-repeat bg-fixed md:bg-origin-border md:h-[80vh] pt-[65px]">
        <h1 className="text-center text-white text-2xl md:text-5xl font-bold uppercase">JJ Kit</h1>
        <p className="text-center text-white text-lg md:text-2xl mt-4">
          Empower Your Team with the Ultimate Agile Project Management Solution
        </p>
        <div className="row--image-style bg-transparent w-full absolute bottom-0 left-0 right-0">
          <svg className="waves w-full h-44" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 300" preserveAspectRatio="none">
            <path fill="rgb(226 232 240)" d="M 1000 280 l 2 -253 c -155 -36 -310 135 -415 164 c -102.64 28.35 -149 -32 -235 -31 c -80 1 -142 53 -229 80 c -65.54 20.34 -101 15 -126 11.61 v 54.39 z"></path>
            <path fill="rgb(100 116 139)" d="M 1000 261 l 2 -222 c -157 -43 -312 144 -405 178 c -101.11 33.38 -159 -47 -242 -46 c -80 1 -153.09 54.07 -229 87 c -65.21 25.59 -104.07 16.72 -126 16.61 v 22.39 z"></path>
            <path fill="#E5E7EB" d="M 1000 300 l 1 -230.29 c -217 -12.71 -300.47 129.15 -404 156.29 c -103 27 -174 -30 -257 -29 c -80 1 -130.09 37.07 -214 70 c -61.23 24 -108 15.61 -126 10.61 v 22.39 z"></path>
          </svg>
        </div>
      </section>

      <section className="w-full pt-10 px-8 bg-gradient-to-r from-blue-100 via-white to-blue-100 relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-200 rounded-full opacity-30 filter blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-purple-200 rounded-full opacity-20 filter blur-3xl"></div>

        <div className="flex flex-col md:flex-row items-center justify-center px-16 relative z-10">
          <div className="md:w-1/2 flex justify-center items-center p-5">
            <div className="bg-white rounded-xl shadow-2xl p-4 hover:scale-105 transform transition-transform duration-300">
              <img
                src="../images/jjkit-new.png"
                alt="car rental software dashboard"
                className="rounded-lg"
              />
            </div>
          </div>
          <div className="md:w-1/2 flex justify-center items-center p-5">
            <div className="bg-white rounded-xl shadow-2xl p-4 hover:scale-105 transform transition-transform duration-300">
              <img
                src="../images/jjkit-logs.png"
                alt="car rental software dashboard"
                className="rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full items-center justify-center bg-gray-200">
        <div className="max-w-screen-lg mx-auto py-8">
          <div className="px-8 md:px-1 md:pr-2 py-6">
            <div className="mb-4 text-lg font-normal text-center md:text-left text-gray-600">
              <p><strong>Industry:</strong> Software Development; Project Management; Collaboration Tools</p>
              <p><strong>Location:</strong> USA</p>
              <p><strong>Areas of expertise:</strong> Agile Project Management; Back-end; Front-end; Quality assurance; Automation</p>
              <p><strong>Timeline:</strong> 2021-now</p>
            </div>

            <p className="text-xl font-normal text-center md:text-left text-gray-600">
              JJ Kit is an all-in-one project management solution designed to streamline complex workflows, enhance team collaboration, and deliver measurable results.
            </p>

            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">Key Features</h2>
              <div className="space-y-6 text-xl text-gray-600">
                {features.map((feature, index) => (
                  <div key={index}>
                    <h3 className="text-2xl font-semibold text-black pb-2">
                      {feature.number}. {feature.title}
                    </h3>
                    <p>{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-12">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">Project Results</h2>
              <p className="text-xl font-normal text-center md:text-left text-gray-600 pb-8">
                JJ Kit has transformed how teams handle projects, enabling faster delivery, improved collaboration, and data-driven decision-making.
              </p>
              <ul className="list-disc pl-5 text-xl font-normal text-center md:text-left text-gray-600">
                <li>Boosted sprint efficiency and backlog prioritization through clear Scrum and Kanban workflows.</li>
                <li>Enhanced team alignment with structured user and role management.</li>
                <li>Increased productivity and faster delivery with seamless project creation and task tracking.</li>
                <li>Informed decision-making with real-time reporting and actionable insights.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <ContactUsComponent />
    </>
  )
}

export default JjKit;